import React, {useState, useEffect} from 'react';
import {Form, Input, Radio, DatePicker, Typography, Divider, FormInstance, Checkbox} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {useNavigate} from "react-router-dom";
import {MembershipCategory} from "../../model/members/MembershipCategory";
import { useAuth0 } from '@auth0/auth0-react';
import { useClient } from '../../common/ClientProvider';
import { useAuth } from '../login/Auth0ProviderWithConfig';
import MembershipCategorySelector from './MembershipCategorySelector';
import { JoiningFeeConfig } from './joiningFee';
import { useFormatCurrency } from '../../hooks/useFormatters';

import { useMember } from '../../common/member/MemberContext';
import { AdminPermissions, MemberPermissions } from '../../model/auth/Permissions';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { calculateProRata } from '../../utils/billingCycle';
import { isValidPhone, phoneNumberRule } from '../../utils/phone';
import dayjs, { Dayjs } from 'dayjs';

const { Item } = Form;
const { Title } = Typography;
const { Text } = Typography;

interface NewMemberCustomFormProps {
    form: FormInstance;
    membershipCategories: MembershipCategory[];
    joiningFee: JoiningFeeConfig;
}

// Custom masked input for sort code
const SortCodeInput: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    // validateForm: () => void;
}> = ({ value = '', onChange }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value.replace(/[^\d]/g, '');
        const formattedValue = inputValue
            .slice(0, 6)
            .replace(/(\d{2})(?=\d)/g, '$1-');
        onChange?.(formattedValue);
        // validateForm();
    };

    return (
        <Input
            value={value}
            onChange={handleChange}
            placeholder="11-22-33"
            maxLength={8}
        />
    );
};

// Custom input for account number (numeric only)
const AccountNumberInput: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    // validateForm: () => void;
}> = ({ value = '', onChange }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value.replace(/[^\d]/g, '');
        onChange?.(numericValue);
    };

    return (
        <Input
            value={value}
            onChange={handleChange}
            placeholder="12345678"
            maxLength={8}
        />
    );
};

const NewMemberCustomForm: React.FC<NewMemberCustomFormProps> = ({ form, membershipCategories, joiningFee }) => {
    const navigate = useNavigate();
    const formatCurrency = useFormatCurrency();

    const [includeJoiningFee, setIncludeJoiningFee] = useState(joiningFee.enabled);
    
    const [selectedMembershipCategory, setSelectedMembershipCategory] = useState<MembershipCategory | null>(null);
    const { isAuthenticated, isLoading } = useAuth0();
    const { email } = useAuth();
    const { currentClientId } = useClient();
    const { isLoading: permissionsLoading, permissions } = useMember();
    const [selectedEmail, setSelectedEmail] = useState<string | undefined>();
    
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate('/login');
      }
    }, [isAuthenticated, isLoading, navigate]);


    // The club a member is created in is the SESSION's tenant, not a form choice:
    // NewMemberRegistration builds the request with `client.currentClientId` and
    // discards this field's value. It used to render a Select whenever the user had
    // more than one club, which both looked like a choice that wasn't one (picking
    // another club still created the member in the current tenant) and left the
    // required field empty until touched. Mirror `currentClientId` instead, and
    // render it read-only below.
    useEffect(() => {
        form.setFieldValue('clientName', currentClientId);
    }, [currentClientId, form]);

    // Default the email field from the current user's permissions. Privileged
    // users (Admin / Front of House / register-any) get an enabled, blank box so
    // they can enter any member's address; everyone else gets a disabled box
    // pre-filled with their own. Keyed on `permissions` so it re-runs once they
    // resolve — otherwise a privileged user would keep the address prefilled
    // during the loading window, when isNotAdminOrFrontOfHouse() is still true.
    useEffect(() => {
        if (permissionsLoading) return;

        if (isNotAdminOrFrontOfHouse()) {
            setSelectedEmail(email);
            form.setFieldValue('email', email);
        } else {
            // Clear to an explicit empty string (not undefined) so the input is
            // reliably blank and the field value stays typed as a string.
            setSelectedEmail('');
            form.setFieldValue('email', '');
        }
        // isNotAdminOrFrontOfHouse is a render-scoped predicate over `permissions`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, permissions, email, form]);

    const [proRataAmount, setProRataAmount] = useState<number | null>(null);
    const { billing, signup } = useClientConfig();
    const collectCompanyName = signup?.collectCompanyName ?? false;

    if (isLoading) {
        return <div>Loading...</div>;
      }

    // Pro-rate the first monthly period using the client's billing cycle
    // (anchor day + cut-off). Mirrors the backend BillingCycleService.
    const calculateProRataAmount = (price: number, startDate: Dayjs | null = null) =>
        calculateProRata(price, startDate ?? dayjs(), billing).amount;

    const handleMembershipCategoryChange = (category: MembershipCategory | null) => {
        setSelectedMembershipCategory(category);
        if (category) {
            let amount;
            
            // Only calculate pro-rata for Monthly payment frequency
            if (category.paymentFrequency === "Monthly") {
                const startDate = form.getFieldValue('startDate');
                amount = calculateProRataAmount(category.amount, startDate);
            } else {
                // For non-Monthly frequencies, use the full membership category amount
                amount = category.amount;
            }
            
            if (includeJoiningFee) {
                amount += joiningFee.amount;
            }
            setProRataAmount(amount);
            form.setFieldsValue({
                membershipCategory: category.name,
                proRataAmount: amount
            });
        }
    };

    const handleJoiningFeeChange = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        setIncludeJoiningFee(checked);

        // Recalculate pro-rata amount with new joining fee state
        const category = membershipCategories.find(cat => cat.name === form.getFieldValue('membershipCategory'));
        if (category) {
            let proRata;
            if (category.paymentFrequency === "Monthly") {
                const startDate = form.getFieldValue('startDate');
                proRata = calculateProRataAmount(category.amount, startDate);
            } else {
                proRata = category.amount;
            }
            if (checked) {
                proRata += joiningFee.amount;
            }
            setProRataAmount(proRata);
            form.setFieldsValue({ proRataAmount: proRata });
        }
    };

    const handleStartDateChange = (date: Dayjs | null) => {
        // Recalculate pro-rata amount when start date changes
        const category = membershipCategories.find(cat => cat.name === form.getFieldValue('membershipCategory'));
        if (category && category.paymentFrequency === "Monthly") {
            let proRata = calculateProRataAmount(category.amount, date);
            if (includeJoiningFee) {
                proRata += joiningFee.amount;
            }
            setProRataAmount(proRata);
            form.setFieldsValue({ proRataAmount: proRata });
        }
    };

    const capitalizeWords = (value: string) => {
        return value.replace(/\b\w/g, char => char.toUpperCase());
    };

    const handleInputChange = (field: string, capitalize: boolean = false) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = e.target;
        if (capitalize) {
            value = capitalizeWords(value);
        }
        form.setFieldsValue({ [field]: value });
        // validateForm();
    };

    const hasAdminPermission = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubAdmin ||
            p.name === AdminPermissions.FortiusAdmin
        );
    };

    const isFrontOfHouse = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.FrontOfHouse ||
            p.name === AdminPermissions.FortiusAdmin
        );
    }

    const hasRegisterAnyPermission = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === MemberPermissions.RegisterAny
        );
    };

    const isNotAdminOrFrontOfHouse = () => {
        return !hasAdminPermission() && !isFrontOfHouse() && !hasRegisterAnyPermission();
    }

    return (
        <Form form={form} layout="vertical"
              initialValues={{
                  country: 'United Kingdom',
                  clientName: currentClientId,
                  includeJoiningFee: joiningFee.enabled,
                  startDate: dayjs()
              }}>
            <Item name="clientName"
                  label="Club Name"
                  rules={[{ required: true, message: 'Please select a club' }]}>
                {/* Read-only: the member lands in the club selected in the header
                    switcher. Switch club there to register into a different one. */}
                <Input disabled data-testid="new-member-club" value={currentClientId} />
            </Item>

            <Item
                name="email"
                label="Email Address"
                rules={[{ required: true, type: 'email', message: 'Please enter a valid email address' }]}
                initialValue={selectedEmail}
            >
                <Input disabled={isNotAdminOrFrontOfHouse()} />
            </Item>
            <Item name="firstName" label="First Name" rules={[{ required: true, message: 'Please enter your first name' }]}>
                <Input onChange={handleInputChange('firstName', true)} />
            </Item>
            <Item name="surname" label="Surname" rules={[{ required: true, message: 'Please enter your surname' }]}>
                <Input onChange={handleInputChange('surname', true)} />
            </Item>
            <Item name="addressLine1" label="Address Line 1" rules={[{ required: true, message: 'Please enter your address' }]}>
                <Input onChange={handleInputChange('addressLine1', true)} />
            </Item>
            <Item name="addressLine2" label="Address Line 2">
                <Input onChange={handleInputChange('addressLine2', true)} />
            </Item>
            <Item name="townCity" label="Town/City" rules={[{ required: true, message: 'Please enter your town or city' }]}>
                <Input onChange={handleInputChange('townCity', true)} />
            </Item>
            <Item name="countyState" label="County/State" rules={[{ required: true, message: 'Please enter your county or state' }]}>
                <Input onChange={handleInputChange('countyState', true)} />
            </Item>
            <Item name="postcode" label="Postcode" rules={[{ required: true, message: 'Please enter your postcode' }]}>
                <Input onChange={handleInputChange('postcode')} />
            </Item>
            <Item name="country" label="Country" rules={[{ required: true, message: 'Please enter your country' }]}>
                <Input onChange={handleInputChange('country', true)} />
            </Item>
            <Item
                name="dateOfBirth"
                label="Date of Birth"
                rules={[{ required: true, message: 'Please enter your date of birth' }]}
            >
                <DatePicker format="DD/MM/YYYY"  />
            </Item>
            <Item
                name="phoneNumber"
                label="Phone Number"
                rules={[{ required: true, message: 'Please enter a phone number' }, phoneNumberRule()]}
            >
                <Input type="tel" onChange={handleInputChange('phoneNumber')} />
            </Item>

            {collectCompanyName && (
                <Item name="companyName" label="Company" data-testid="new-member-company">
                    <Input data-testid="new-member-company-input" />
                </Item>
            )}

            <Item
                name="interests"
                label="Interests"
                rules={[
                    {
                        validator: (_, value) => {
                            if (value && Array.isArray(value) && value.length > 0) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Please select at least one interest'));
                        }
                    }
                ]}
            >
                <Checkbox.Group>
                    <Checkbox value="Squash">Squash</Checkbox>
                    <Checkbox value="Fitness">Fitness</Checkbox>
                    <Checkbox value="Classes">Classes</Checkbox>
                </Checkbox.Group>
            </Item>

            <Divider />
            <Title level={3}>Emergency Contact</Title>

            <Item
                name="emergencyContactName"
                label="Emergency Contact Name"
                rules={[{ required: true, message: 'Please enter the emergency contact name' }]}
            >
                <Input onChange={handleInputChange('emergencyContactName', true)} />
            </Item>
            <Item
                name="emergencyContactPhone"
                label="Emergency Contact Phone Number"
                dependencies={['emergencyContactEmail']}
                rules={[
                    {
                        validator: (_, value) => {
                            const email = form.getFieldValue('emergencyContactEmail');
                            if (!value && !email) {
                                return Promise.reject(new Error('Please enter a phone number or email address'));
                            }
                            if (value && !isValidPhone(value)) {
                                return Promise.reject(new Error('Please enter a valid phone number'));
                            }
                            return Promise.resolve();
                        }
                    }
                ]}
            >
                <Input type="tel" onChange={handleInputChange('emergencyContactPhone')} />
            </Item>
            <Item
                name="emergencyContactEmail"
                label="Emergency Contact Email Address"
                dependencies={['emergencyContactPhone']}
                rules={[
                    {
                        validator: (_, value) => {
                            const phone = form.getFieldValue('emergencyContactPhone');
                            if (!value && !phone) {
                                return Promise.reject(new Error('Please enter a phone number or email address'));
                            }
                            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                return Promise.reject(new Error('Please enter a valid email address'));
                            }
                            return Promise.resolve();
                        }
                    }
                ]}
            >
                <Input onChange={handleInputChange('emergencyContactEmail')} />
            </Item>

            <Item
                name="specialOffers"
                label="Would you like to receive information about special offers and upcoming events at the club?"
                rules={[{ required: true, message: 'Please select an option' }]}
            >
                <Radio.Group >
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                </Radio.Group>
            </Item>

            <Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true, message: 'Please select a start date' }]}
            >
                <DatePicker 
                    format="DD/MM/YYYY" 
                    onChange={handleStartDateChange}
                   
                />
            </Item>

            <Item
                name="membershipCategory"
                label="Membership Category"
                rules={[{ required: true, message: 'Please select a membership category' }]}
            >
                <MembershipCategorySelector
                    membershipCategories={membershipCategories}
                    onChange={handleMembershipCategoryChange}
                    value={form.getFieldValue('membershipCategory')}
                />
            </Item>

            {joiningFee.enabled && (
                <Item
                    name="includeJoiningFee"
                    valuePropName="checked"
                >
                    <Checkbox onChange={handleJoiningFeeChange} disabled={isNotAdminOrFrontOfHouse()}>
                        {joiningFee.label} ({formatCurrency(joiningFee.amount)})
                    </Checkbox>
                </Item>
            )}

            <Item name="proRataAmount" hidden>
                <Input />
            </Item>

            {proRataAmount !== null && selectedMembershipCategory && (
                <Item>
                    <Text strong style={{ fontSize: '18px' }}>
                        {selectedMembershipCategory.paymentFrequency === 'Monthly' 
                            ? 'Pro-rata amount due today' 
                            : 'Membership amount due today'
                        }: {formatCurrency(proRataAmount)}
                    </Text>
                </Item>
            )}

            <Divider />

            <Title level={3}>Direct Debit Setup</Title>

            <Item name="bankName" label="Bank Name" rules={[{ required: true, message: 'Please enter the bank name' }]}>
                <Input onChange={handleInputChange('bankName', true)} />
            </Item>
            <Item name="accountHolderName" label="Name of Account Holder" rules={[{ required: true, message: 'Please enter the account holder name' }]}>
                <Input onChange={handleInputChange('accountHolderName', true)} />
            </Item>
            <Form.Item
                name="sortCode"
                label="Sort Code"
                rules={[
                    { required: true, message: 'Please enter the sort code' },
                    { pattern: /^\d{2}-\d{2}-\d{2}$/, message: 'Please enter a valid sort code' }
                ]}
            >
                <SortCodeInput onChange={handleInputChange}/>
            </Form.Item>

            <Item
                name="accountNumber"
                label="Account Number"
                rules={[
                    { required: true, message: 'Please enter the account number' },
                    { pattern: /^\d{8}$/, message: 'Please enter a valid 8-digit account number' }
                ]}
            >
                <AccountNumberInput onChange={handleInputChange} />
            </Item>
        </Form>
    );
};

export default NewMemberCustomForm;



